<template>
  <div v-if="isEmptyFilteredVwTest" class="px-3">
    <div class="grid grid-cols-2 gap-2">
      <!-- Zoomer Col1 Tests -->
      <VwTestCategoryColumnCard
        v-if="zoomerCategory"
        :test-node-list="zoomerCol1Tests"
        use-new-styling
        :select-prop="selectProp"
        :is-not-allowed-select-prop="isNotAllowedSelectProp"
        :order-test-support-obj="orderTestSupportObj"
      />
      <!-- Zoomer Col2 Tests -->
      <VwTestCategoryColumnCard
        v-if="zoomerCategory && zoomerCol2Tests.length > 0"
        :test-node-list="zoomerCol2Tests"
        use-new-styling
        :select-prop="selectProp"
        :is-not-allowed-select-prop="isNotAllowedSelectProp"
        :order-test-support-obj="orderTestSupportObj"
      />
      <!-- Genetics -->
      <VwTestCategoryColumn
        v-if="filteredGeneticsCategory"
        :categorized-vw-list="[filteredGeneticsCategory]"
        use-new-styling
        :select-prop="selectProp"
        :is-not-allowed-select-prop="isNotAllowedSelectProp"
        :order-test-support-obj="orderTestSupportObj"
      />
      <!-- Infections -->
      <VwTestCategoryColumn
        v-if="infectionsCategory"
        :categorized-vw-list="[infectionsCategory]"
        use-new-styling
        :select-prop="selectProp"
        :is-not-allowed-select-prop="isNotAllowedSelectProp"
        :order-test-support-obj="orderTestSupportObj"
      />
    </div>
  </div>
  <TestCard
    v-else
    :key="rootForSearchedVwTest.id"
    class="ml-2.5 w-1/3 searched-test"
    :test-group-node="rootForSearchedVwTest"
    :filtered-test="searchedVwTest"
    :is-show-expand="true"
    :select-prop="selectProp"
    :is-not-allowed-select-prop="isNotAllowedSelectProp"
    :order-test-support-obj="orderTestSupportObj"
  />
</template>

<script>
import VwTestCategoryColumn from "./vw-test-tab-pane/components/VwTestCategoryColumn.vue";
import VwTestCategoryColumnCard from "./vw-test-tab-pane/components/VwTestCategoryColumnCard.vue";
import TestCard from "./TestCard.vue";
import _ from "lodash";
import { mapState } from 'vuex';
import { LANGUAGE_MAP } from "@/utils/const";
import { BLOOD_SALIVA_ADDON_IDS } from "@/utils/va-order-test/testAndPanelSelection/geneticAddonConstants.js";

export default {
  name: "NewVwTestTabPane",
  components: {
    VwTestCategoryColumn,
    VwTestCategoryColumnCard,
    TestCard
  },
  props: {
    rootForSearchedVwTest: {
      type: Object,
      default: () => {}
    },
    searchedVwTest: {
      type: Object,
      default: () => {}
    },
    selectProp: {
      type: String,
      default: "isSelect"
    },
    isNotAllowedSelectProp: {
      type: String,
      default: "isNotAllowedSelect"
    },
    orderTestSupportObj: {
      type: Object,
      default: () => {}
    },
    serumCategorizedVwTests: {
      type: Array,
      default: () => [],
    },
    dbsCategorizedVwTests: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState("orderTest", ["isDbsVersion"]),
    ...mapState(["language"]),

    isEmptyFilteredVwTest() {
      return _.isEmpty(this.rootForSearchedVwTest);
    },
    
    sortedCategorizedVwList() {
      return this.isDbsVersion ? this.dbsCategorizedVwTests : this.serumCategorizedVwTests;
    },
    
    zoomerCategory() {
      return this.sortedCategorizedVwList.find(category => {
        const categoryName = category.categoryName?.toLowerCase() || "";
        return categoryName === "zoomers";
      });
    },

    zoomerCol1Tests() {
      if (!this.zoomerCategory) return [];
      const col1Ids = this.isDbsVersion 
        ? [844, 854, 850, 665]  // DBS version
        : [844, 854, 842, 830, 178, 853];  // Serum version
      const filtered = this.zoomerCategory.testNodeList.filter(node => col1Ids.includes(node.id));
      // Sort by the order specified in col1Ids
      return _.sortBy(filtered, node => _.indexOf(col1Ids, node.id));
    },

    zoomerCol2Tests() {
      if (!this.zoomerCategory) return [];
      const col2Ids = this.isDbsVersion 
        ? [845, 846, 860]  // DBS version
        : [845, 849, 846, 859];  // Serum version
      const filtered = this.zoomerCategory.testNodeList.filter(node => col2Ids.includes(node.id));
      // Sort by the order specified in col2Ids
      return _.sortBy(filtered, node => _.indexOf(col2Ids, node.id));
    },
    
    geneticsCategory() {
      return this.sortedCategorizedVwList.find(category => {
        const categoryName = category.categoryName?.toLowerCase() || "";
        return categoryName === "genetics";
      });
    },

    filteredGeneticsCategory() {
      if (!this.geneticsCategory || !this.geneticsCategory.testNodeList) {
        return null;
      }

      const testNodeList = this.geneticsCategory.testNodeList || [];
      const notAllowedProp = this.isNotAllowedSelectProp;

      // Eligible sample type set: 'blood' and/or 'saliva' if any version of that type is eligible
      const eligibleSampleTypes = new Set();
      Object.values(BLOOD_SALIVA_ADDON_IDS).forEach(({ blood: bloodId, saliva: salivaId }) => {
        const bloodNode = testNodeList.find(node => node.id === bloodId);
        const salivaNode = testNodeList.find(node => node.id === salivaId);
        const isEligible = (node) => node?.value?.[notAllowedProp] === false;
        if (isEligible(bloodNode)) eligibleSampleTypes.add("blood");
        if (isEligible(salivaNode)) eligibleSampleTypes.add("saliva");
      });

      // Show blood when no one is eligible (empty set) OR when the eligible set contains blood
      const showBloodVersions = !eligibleSampleTypes.size || eligibleSampleTypes.has("blood");
      // DBS version always keeps saliva addons even when showBloodVersions would be true
      const useBloodForAddons = !this.isDbsVersion && showBloodVersions;

      // Filter testNodeList: keep blood versions if useBloodForAddons is true, otherwise keep saliva versions
      const filteredTestNodeList = testNodeList.filter(node => {
        const nodeId = node.id;
        
        // Check if this is a blood or saliva version of a genetic addon
        const isBloodAddon = Object.values(BLOOD_SALIVA_ADDON_IDS).some(
          ({ blood }) => blood === nodeId
        );
        const isSalivaAddon = Object.values(BLOOD_SALIVA_ADDON_IDS).some(
          ({ saliva }) => saliva === nodeId
        );
        
        // Keep non-genetic addon tests
        if (!isBloodAddon && !isSalivaAddon) {
          return true;
        }

        // For genetic addons, show blood only when useBloodForAddons (non-DBS + showBloodVersions), otherwise show saliva
        return useBloodForAddons ? isBloodAddon : isSalivaAddon;
      });

      // Return a new category object with filtered testNodeList
      return {
        ...this.geneticsCategory,
        testNodeList: filteredTestNodeList
      };
    },

    infectionsCategory() {
      return this.sortedCategorizedVwList.find(category => {
        const categoryName = category.categoryName?.toLowerCase() || "";
        return categoryName === "infections";
      });
    }
  },
  methods: {
    getCategoryNameByLanguage(categoryNames) {
      let language = LANGUAGE_MAP[this.language];
      return categoryNames[language] || categoryNames.english;
    }
  }
}
</script>

<style scoped>
.searched-test {
  border: 2px solid #3498db;
  box-shadow: 0px 2px 6px rgba(48, 48, 48, 0.13);
}
</style>

